import {endOfMonth} from 'date-fns/endOfMonth'
import {startOfMonth} from 'date-fns/startOfMonth'
import {append, identity, max, min, pick, reduce, sortBy} from 'ramda'

import {AvailabilityHotelEntity} from '../../availability/types'
import {
  dateFormat,
  dateStringToMiddayDate,
  UTS_DATE_FORMAT
} from '../../utils/date'
import {percentile} from '../../utils/number'

export const INITIAL_FETCH_WAIT_TIME = 4000 // ms
export const INITIAL_FETCH_WAIT_TIME_FOR_NO_DATES_TRAFFIC = 0 // ms
export const MAX_DATES_TO_SHOW_DESKTOP = 9 // days
export const MAX_DATES_TO_SHOW_MOBILE = 4 // days

export enum PRICE_TYPE {
  HIGH = 'high',
  NEUTRAL = 'neutral',
  LOW = 'low'
}

/**
 * Extracts prices from hotel availability data.
 * @param hotelAvailabilityPrices - Hotel availability entity containing price data
 * @returns Array of cheapest nightly rates
 */
const getPrices = (hotelAvailabilityPrices: AvailabilityHotelEntity) => {
  const prices = Object.keys(hotelAvailabilityPrices).map(date => {
    return hotelAvailabilityPrices[date]?.cheapestNightlyRate
  })
  return prices
}

const getMinMaxPrices = (
  hotelAvailabilityPrices: AvailabilityHotelEntity
): {minPrice: number; maxPrice: number} => {
  const prices = getPrices(hotelAvailabilityPrices)
  const minPrice = reduce(min, Infinity, prices) as number
  const maxPrice = reduce(max, -Infinity, prices) as number
  return {minPrice, maxPrice}
}

const CHEAP_PRICE_PERCENTILE = 25
const EXPENSIVE_PRICE_PERCENTILE = 75

// Midday, not midnight: it keeps the date on the intended day either side of a
// DST shift, which matters for the month boundaries derived from it.
const getCurrentMonthRange = (currentDate: string) => {
  const currentMiddayDate = dateStringToMiddayDate(currentDate)
  return {
    start: startOfMonth(currentMiddayDate),
    end: endOfMonth(currentMiddayDate)
  }
}

const getComparisonPrices = (
  currentDate: string,
  hotelAvailabilityPrices: AvailabilityHotelEntity,
  comparisonDateRange?: {start: Date; end: Date}
) => {
  // Availability keys are yyyy-MM-dd, so the bounds are formatted once and the
  // keys compared as strings. Parsing every key into a Date to compare it is
  // ~50x slower and this runs per key, per rendered day cell.
  const {start, end} = comparisonDateRange ?? getCurrentMonthRange(currentDate)
  const rangeStart = dateFormat(start, UTS_DATE_FORMAT)
  const rangeEnd = dateFormat(end, UTS_DATE_FORMAT)

  return Object.keys(hotelAvailabilityPrices).reduce((acc: number[], d) => {
    if (rangeStart <= d && d <= rangeEnd) {
      return append(hotelAvailabilityPrices[d]?.cheapestNightlyRate, acc)
    }
    return acc
  }, [])
}

/**
 * Calculates the price type (low, neutral, high) based on monthly price distribution.
 * @param hotelAvailabilityPrices - Hotel availability entity containing price data
 * @param price - The price to categorize
 * @param date - The date for monthly price comparison
 * @param comparisonDateRange - Optional date range for price type calculations
 * @returns The price type classification
 */
export const calculatePriceType = (
  hotelAvailabilityPrices: AvailabilityHotelEntity,
  price: number,
  date: string,
  comparisonDateRange?: {start: Date; end: Date}
) => {
  const comparisonPrices = getComparisonPrices(
    date,
    hotelAvailabilityPrices,
    comparisonDateRange
  )
  const percentileValue = percentile(comparisonPrices, price)

  const priceType =
    percentileValue < CHEAP_PRICE_PERCENTILE
      ? PRICE_TYPE.LOW
      : percentileValue < EXPENSIVE_PRICE_PERCENTILE
        ? PRICE_TYPE.NEUTRAL
        : PRICE_TYPE.HIGH

  return priceType
}

export interface DayPriceInfo {
  price?: number
  currency?: string
  priceType: PRICE_TYPE
  displayPrice?: number
}

interface DayPriceInfoOptions {
  /** Show the total stay price (rate breakdown sum) instead of the nightly rate */
  showTotalPrices?: boolean
  /** Downgrade high prices to neutral (no "expensive" highlighting) */
  noHighPrices?: boolean
  /** Range for price type calculations, defaults to current month */
  comparisonDateRange?: {start: Date; end: Date}
}

/**
 * Derives everything a calendar day cell shows for a date: the nightly price,
 * its currency, the low/neutral/high classification, and the price to display.
 * Single source of truth for both the rendered cell and markup derived from it
 * (aria-labels, data attributes), so the two cannot drift apart.
 * @param hotelAvailabilityPrices - Hotel availability entity containing price data
 * @param date - The date to derive prices for (yyyy-MM-dd)
 * @param options - Display options, see {@link DayPriceInfoOptions}
 * @returns The day's price details, see {@link DayPriceInfo}
 */
export const getDayPriceInfo = (
  hotelAvailabilityPrices: AvailabilityHotelEntity | undefined,
  date: string,
  {
    showTotalPrices = false,
    noHighPrices = false,
    comparisonDateRange
  }: DayPriceInfoOptions = {}
): DayPriceInfo => {
  if (!hotelAvailabilityPrices) {
    return {
      price: 0,
      currency: '',
      priceType: PRICE_TYPE.NEUTRAL,
      displayPrice: 0
    }
  }

  const price = hotelAvailabilityPrices[date]?.cheapestNightlyRate
  const currency = hotelAvailabilityPrices[date]?.offers[0]?.currency

  let priceType = PRICE_TYPE.NEUTRAL
  if (price) {
    const type = calculatePriceType(
      hotelAvailabilityPrices,
      price,
      date,
      comparisonDateRange
    )
    priceType =
      type === PRICE_TYPE.HIGH && noHighPrices ? PRICE_TYPE.NEUTRAL : type
  }

  const rateBreakdown =
    hotelAvailabilityPrices[date]?.cheapestPriceRateBreakdown
  const displayPrice =
    showTotalPrices && rateBreakdown
      ? rateBreakdown.base + rateBreakdown.taxes + rateBreakdown.hotelFees
      : price

  return {price, currency, priceType, displayPrice}
}

/**
 * Determines if the given price is the cheapest among available dates.
 * @param hotelAvailabilityPrices - Hotel availability entity containing price data
 * @param price - The price to check
 * @param dates - Optional array of specific dates to consider
 * @returns True if the price is the cheapest available
 */
export const getIsCheapestPrice = (
  hotelAvailabilityPrices: AvailabilityHotelEntity,
  price: number,
  dates?: string[]
) => {
  const selectedDatesAvailabilityPrices = dates
    ? pick(dates, hotelAvailabilityPrices)
    : hotelAvailabilityPrices
  const {minPrice} = getMinMaxPrices(selectedDatesAvailabilityPrices)

  return Boolean(price === minPrice)
}

const sortAlphabetically = sortBy(identity)

/**
 * Returns the dates to display in the availability pane.
 * Pick dates before and after the check in date, if there are enough available dates.
 * Otherwise, pick dates after the check in date.
 * @param hotelAvailabilityPrices - available prices for the hotel
 * @param checkIn - check in date
 * @param maxDatesToShow - maximum number of dates to show
 * @returns string[] - dates to display
 */
export const getDatesToDisplay = (
  hotelAvailabilityPrices: AvailabilityHotelEntity,
  checkIn: string,
  maxDatesToShow: number
): string[] => {
  const hotelAvailabilityDates = Object.keys(hotelAvailabilityPrices)
  const dates = [...new Set([...hotelAvailabilityDates, checkIn])]
  const sortedDates = sortAlphabetically(dates)
  const checkInIndex = sortedDates.findIndex(d => d === checkIn)
  const datesToDisplay: string[] = []

  Array.from(Array(maxDatesToShow), (_, i) => {
    const index = i + 1
    const nextDate = sortedDates[checkInIndex + index]
    const previousDate = sortedDates[checkInIndex - index]

    // add half the dates before the check in date, if there are enough available dates
    if (previousDate && datesToDisplay.length <= maxDatesToShow / 2) {
      datesToDisplay.push(previousDate)
    }
    // and fill the rest of the array with dates after the check in date
    if (nextDate && datesToDisplay.length < maxDatesToShow) {
      datesToDisplay.push(nextDate)
    }
  })

  return sortAlphabetically(datesToDisplay)
}

/**
 * Gets the availability status for a specific date.
 * @param params - Parameters object
 * @param params.hotelAvailabilityPrices - Hotel availability entity containing price data
 * @param params.date - The date to check availability for
 * @param params.searchComplete - Whether the search is complete
 * @returns The availability status (price type, 'loading', or 'unavailable')
 */
export const getAvailabilityStatus = ({
  hotelAvailabilityPrices,
  date,
  searchComplete
}: {
  hotelAvailabilityPrices?: AvailabilityHotelEntity
  date: string
  searchComplete: boolean
}) => {
  const isDateAvailable =
    hotelAvailabilityPrices && Boolean(hotelAvailabilityPrices[date])
  if (isDateAvailable)
    return calculatePriceType(
      hotelAvailabilityPrices,
      hotelAvailabilityPrices[date]?.cheapestNightlyRate,
      date
    )
  if (!searchComplete) return 'loading'
  return 'unavailable'
}
